"use client";

import { c as _c } from "react/compiler-runtime";
import { publicEnv } from "@/config/env";
import { getConfiguredIpfsGatewayHost } from "@/lib/media/ipfs-gateways";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import IpfsService from "./IPFSService";
interface IpfsContextType {
  ipfsService: IpfsService | null;
}
const IpfsContext = createContext<IpfsContextType | undefined>(undefined);
const readIpfsConfig = () => {
  const apiEndpoint = publicEnv.IPFS_API_ENDPOINT;
  const gatewayEndpoint = publicEnv.IPFS_GATEWAY_ENDPOINT;
  if (!apiEndpoint || !gatewayEndpoint) {
    throw new Error("Missing IPFS_API_ENDPOINT or IPFS_GATEWAY_ENDPOINT");
  }
  let trimmed = gatewayEndpoint;
  while (trimmed.endsWith("/")) {
    trimmed = trimmed.slice(0, -1);
  }
  const gatewayBase = trimmed.endsWith("/ipfs") ? trimmed.slice(0, -5) : trimmed;
  const mfsPath = publicEnv.IPFS_MFS_PATH;
  return {
    apiEndpoint,
    gatewayEndpoint: trimmed,
    gatewayBase,
    mfsPath
  } as const;
};
const getEnv = async () => readIpfsConfig();
export const IpfsProvider: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(8);
  const {
    children
  } = t0;
  const [ipfsService, setIpfsService] = useState(null);
  let t1;
  let t2;
  if ($[0] !== ipfsService) {
    t1 = () => {
      if (ipfsService) {
        return;
      }
      getEnv().then(info => {
        const service = new IpfsService({
          apiEndpoint: info.apiEndpoint,
          mfsPath: info.mfsPath
        });
        service.init();
        setIpfsService(service);
      }).catch(_temp);
    };
    t2 = [ipfsService];
    $[0] = ipfsService;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== ipfsService) {
    t3 = {
      ipfsService
    };
    $[3] = ipfsService;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const value = t3;
  let t4;
  if ($[5] !== children || $[6] !== value) {
    t4 = <IpfsContext.Provider value={value}>{children}</IpfsContext.Provider>;
    $[5] = children;
    $[6] = value;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
export const useIpfsService = () => {
  const context = useContext(IpfsContext);
  if (!context?.ipfsService) {
    throw new Error("useIpfsService must be used within an IpfsProvider");
  }
  return context.ipfsService;
};
function joinUrlPaths(basePathname: string, pathName: string): string {
  const normalizedBase = basePathname.endsWith("/") ? basePathname.slice(0, -1) : basePathname;
  const normalizedPath = pathName.startsWith("/") ? pathName : `/${pathName}`;
  if (!normalizedBase) {
    return normalizedPath;
  }
  return `${normalizedBase}${normalizedPath}`;
}
export const resolveIpfsUrlSync = (url: string) => {
  try {
    const {
      gatewayBase
    } = readIpfsConfig();
    if (url.startsWith("ipfs://")) {
      return `${gatewayBase}/ipfs/${url.slice(7)}`;
    }
    const configuredHost = getConfiguredIpfsGatewayHost();
    if (!configuredHost) {
      return url;
    }
    const configuredGatewayBase = new URL(gatewayBase);
    const parsedUrl = new URL(url);
    const normalizedHost = parsedUrl.hostname.toLowerCase();
    if (normalizedHost !== "ipfs.io" && normalizedHost !== "www.ipfs.io") {
      return url;
    }
    if (!parsedUrl.pathname.startsWith("/ipfs/")) {
      return url;
    }
    parsedUrl.protocol = configuredGatewayBase.protocol;
    parsedUrl.hostname = configuredGatewayBase.hostname;
    parsedUrl.port = configuredGatewayBase.port;
    parsedUrl.pathname = joinUrlPaths(configuredGatewayBase.pathname, parsedUrl.pathname);
    return parsedUrl.toString();
  } catch (error) {
    console.error("Error resolving IPFS URL", error);
    return url;
  }
};
export const resolveIpfsUrl = (url: string) => {
  return resolveIpfsUrlSync(url);
};
function _temp(error) {
  console.error("Error initializing IPFS service", error);
}